"use client";

import { useEffect, useId } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ExternalLink, ArrowRight } from "lucide-react";
import { useTranslations } from "next-intl";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onContinue: () => void;
}

export default function ExternalLinkModal({
  isOpen,
  onClose,
  onContinue,
}: Props) {
  const t = useTranslations("externalLinkModal");
  const titleId = useId();

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEsc);
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.removeEventListener("keydown", handleEsc);
      document.body.style.overflow = "unset";
    };
  }, [onClose, isOpen]);

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <AnimatePresence mode="wait">
      {isOpen && (
        <motion.div
          onClick={handleBackdropClick}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
        >
          <motion.div
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
            initial={{ scale: 0.97, opacity: 0, y: 12 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.97, opacity: 0, y: 12 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
            className="relative bg-white w-full max-w-xl mx-auto rounded-2xl border border-gray-200 shadow-2xl p-6 md:p-7"
          >
            <button
              onClick={onClose}
              aria-label="Cerrar"
              className="absolute top-4 right-4 text-gray-400 hover:text-[#102542] hover:bg-gray-100 rounded-full p-1.5 transition-colors cursor-pointer"
            >
              <X className="w-4 h-4" />
            </button>

            <div className="flex items-start gap-4 mb-4 pr-8">
              <span className="shrink-0 inline-flex items-center justify-center w-10 h-10 rounded-full bg-[#102542]/5 text-[#102542]">
                <ExternalLink className="w-4 h-4" />
              </span>
              <h2
                id={titleId}
                className="text-lg md:text-xl font-bold text-gray-900 tracking-tight text-balance leading-snug"
              >
                {t("title")}
              </h2>
            </div>

            <p className="text-sm text-gray-600 leading-relaxed mb-6">
              {t("description")}
            </p>

            {/* col-reverse en móvil: la acción principal queda arriba */}
            <div className="flex flex-col-reverse sm:flex-row gap-2.5">
              <button
                onClick={onClose}
                className="flex-1 inline-flex items-center justify-center px-4 py-2.5 rounded-full border border-gray-200 text-gray-700 text-sm font-semibold hover:border-[#102542] hover:text-[#102542] transition-colors cursor-pointer"
              >
                {t("stayButtonLabel")}
              </button>
              <button
                onClick={onContinue}
                className="group flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full bg-[#102542] text-white text-sm font-semibold hover:bg-[#0a1a30] transition-colors cursor-pointer"
              >
                {t("buttonLabel")}
                <ArrowRight className="w-3.5 h-3.5 transition-transform duration-300 group-hover:translate-x-0.5" />
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
